import React, { useMemo, useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { useLocation } from 'react-router-dom';

import Sidebar from './Sidebar';
import ExploreSidebar from './ExploreSidebar';

import logo from '../assets/image/logo.png';
import { EmailSVG, PhoneSVG, GridSVG } from '../components/SvgIcons';
import { isEnabled } from '../lib/promoFlags';

function Header({
  promoData = null,
  callbackEnabled = false,
  enquiryEnabled = true,
  contactEnabled = true,
}) {
  const location = useLocation();
  const baseRoute = useMemo(
    () => `${location.pathname}${location.search || ''}`,
    [location.pathname, location.search]
  );
  const menuItems = useMemo(
    () => [
      { href: `${baseRoute}#home`, label: 'Home' },
      { href: `${baseRoute}#tours`, label: 'Tours' },
      { href: `${baseRoute}#similar-tours`, label: 'Similar Tours' },
      { href: `${baseRoute}#inquire-now`, label: 'Inquire Now' },
      { href: `${baseRoute}#contacts`, label: 'Contacts' },
    ].filter((item) => {
      if (item.label === 'Inquire Now') {
        return isEnabled(enquiryEnabled, true);
      }

      if (item.label === 'Contacts') {
        return isEnabled(contactEnabled, true);
      }

      return true;
    }),
    [baseRoute, contactEnabled, enquiryEnabled]
  );

  const socialLinks = [
    { href: promoData?.social?.facebook, icon: 'fa-facebook-f', label: 'Facebook' },
    { href: promoData?.social?.twitter, icon: 'fa-x-twitter', label: 'X' },
    { href: promoData?.social?.instagram, icon: 'fa-instagram', label: 'Instagram' },
    { href: promoData?.social?.pinterest, icon: 'fa-pinterest-p', label: 'Pinterest' },
    { href: promoData?.social?.linkedin, icon: 'fa-linkedin-in', label: 'LinkedIn' },
    { href: promoData?.social?.youtube, icon: 'fa-youtube', label: 'YouTube' },
    { href: promoData?.social?.google, icon: 'fa-google', label: 'Google' },
    { href: promoData?.social?.flickr, icon: 'fa-flickr', label: 'Flickr' },
  ].filter((item) => item.href);

  const brandLogo = promoData?.promo?.logo_url || logo;
  const brandName = promoData?.promo?.company?.name || 'TripRex';
  const topEmail = promoData?.contact?.email_primary || 'info@classicbharatholidays.com';
  const callNumber = promoData?.enquiry?.callback_mobile || promoData?.contact?.mobile_primary || '00 (888)+123456';
  const showCallback = isEnabled(callbackEnabled, false) && String(callNumber || '').trim().length > 0;
  const showContact = isEnabled(contactEnabled, true);

  const [exploreOpen, setExploreOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleExploreToggle = () => {
    setExploreOpen(!exploreOpen);
  };

  const handleMenuToggle = () => {
    setMenuOpen(!menuOpen);
  };

  const handleExploreClose = () => {
    setExploreOpen(false);
  };

  return (
    <>
      <div className="top-header">
        <Container>
          <Row className="align-items-center">
            {showContact ? (
              <Col sm={3} className="d-flex justify-content-start">
                <a href={`mailto:${topEmail}`} className="text-white">
                  <div className="d-flex gap-2 align-items-center">
                    <EmailSVG />
                    <div>
                      <h6 className="mb-0 fw-normal font-12">Email</h6>
                      <p className="mb-0 fw-semibold font-15">{topEmail}</p>
                    </div>
                  </div>
                </a>
              </Col>
            ) : null}
            <Col sm={showContact ? 6 : 9} className={`text-center ${showContact ? '' : 'text-sm-start'}`}>
              <h4 className="text-white font-14 fw-semibold">
                {promoData?.hero?.tagline || '50% Off Your Next Trip. Hurry Up For your new Tour! Book Your Tour'}
              </h4>
            </Col>
            <Col sm={3} className="d-flex justify-content-end">
              <ul className="social-icons mb-0">
                {socialLinks.map((social) => (
                  <li key={social.label}>
                    <a href={social.href} target="_blank" rel="noreferrer" aria-label={social.label}>
                      <i className={`fa-brands ${social.icon}`} aria-hidden="true"></i>
                      <span className="visually-hidden">{social.label}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </Col>
          </Row>
        </Container>
      </div>

      <div className="main-header">
        <Container>
          <div className="nav-wrap d-flex align-items-center justify-content-between">
            <div className="brand d-flex align-items-center">
              <img src={brandLogo} alt={brandName} className="brand-logo" />
              <span className="brand-text"></span>
            </div>

            <nav className="main-nav">
              <ul className="menu-list">
                {menuItems.map((item) => (
                  <li key={item.label} className="menu-item">
                    <a href={item.href}>{item.label}</a>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="right-header d-flex align-items-center">
              <div className="icons d-flex align-items-center ">
                <div className="grid-icon" onClick={handleExploreToggle} style={{ cursor: 'pointer' }}>
                  <GridSVG />
                </div>
                <button className="hamburger-btn d-lg-none" onClick={handleMenuToggle} aria-label="Open menu">
                  <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25">
                    <path d="M0 4.46439C0 4.70119 0.0940685 4.92829 0.261511 5.09574C0.428955 5.26318 0.656057 5.35725 0.892857 5.35725H24.1071C24.3439 5.35725 24.571 5.26318 24.7385 5.09574C24.9059 4.92829 25 4.70119 25 4.46439C25 4.22759 24.9059 4.00049 24.7385 3.83305C24.571 3.6656 24.3439 3.57153 24.1071 3.57153H0.892857C0.656057 3.57153 0.428955 3.6656 0.261511 3.83305C0.0940685 4.00049 0 4.22759 0 4.46439ZM4.46429 11.6072H24.1071C24.3439 11.6072 24.571 11.7013 24.7385 11.8688C24.9059 12.0362 25 12.2633 25 12.5001C25 12.7369 24.9059 12.964 24.7385 13.1315C24.571 13.2989 24.3439 13.393 24.1071 13.393H4.46429C4.22749 13.393 4.00038 13.2989 3.83294 13.1315C3.6655 12.964 3.57143 12.7369 3.57143 12.5001C3.57143 12.2633 3.6655 12.0362 3.83294 11.8688C4.00038 11.7013 4.22749 11.6072 4.46429 11.6072ZM12.5 19.643H24.1071C24.3439 19.643 24.571 19.737 24.7385 19.9045C24.9059 20.0719 25 20.299 25 20.5358C25 20.7726 24.9059 20.9997 24.7385 21.1672C24.571 21.3346 24.3439 21.4287 24.1071 21.4287H12.5C12.2632 21.4287 12.0361 21.3346 11.8687 21.1672C11.7012 20.9997 11.6071 20.7726 11.6071 20.5358C11.6071 20.299 11.7012 20.0719 11.8687 19.9045C12.0361 19.737 12.2632 19.643 12.5 19.643Z"></path>
                  </svg>
                </button>
              </div>
              {showContact && showCallback ? (
                <div className="call-box d-flex align-items-center ">
                  <span className="call-icon">
                    <PhoneSVG />
                  </span>
                  <div className="inquiry">
                    <div className="call-caption fw-normal font-12">To More Inquiry</div>
                    <a href={`tel:${callNumber}`} className="call-number fw-semibold font-15">{callNumber}</a>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </Container>
      </div>

      <ExploreSidebar isOpen={exploreOpen} onClose={handleExploreClose} promoData={promoData} logoUrl={brandLogo} />
      <Sidebar isOpen={menuOpen} onClose={() => setMenuOpen(false)} logoUrl={brandLogo} brandName={brandName} menuItems={menuItems} />
    </>
  );
}

export default Header;
